import {
  Box,
  Card,
  Flex,
  Icon,
  Text,
  Button,
  Heading,
  useTheme,
  CardBody,
  FormLabel,
  LinkButton,
  FormControl,
  CurrencyInput
} from '@homebotapp/hb-react-component-catalog'
import { useState } from 'react'
import { match } from 'react-router'
import history from '../../../history'
import { useSelector } from 'react-redux'
import { BuyerInfo } from '../../../types/buyerInfo'
import { useIntl, defineMessages } from 'react-intl'
import { AppLayout } from '../../../App/Layout/AppLayout'
import monthlyPaymentBg from '../assets/monthly-payment-bg.png'
import { SEARCH_PATH_HELPERS } from '../../../constants/navigation'
import { selectBuyerInfo } from '../../../store/selectors/buyerInfo'
import { selectClientIsBuyer } from '../../../store/selectors/client'
import monthlyPaymentBgLight from '../assets/monthly-payment-bg-light.png'
import { EstimatedMonthlyPayment } from '../../../Components/homeSearch/ListingDetails/EstimatedMonthlyPayment/EstimatedMonthlyPayment'
import { EstimatedMonthlyPaymentBreakdownCustomerDM } from '../../../Components/homeSearch/ListingDetails/EstimatedMonthlyPayment/EstimatedMonthlyPaymentBreakdown/EstimatedMonthlyPaymentBreakdownCustomerDM/EstimatedMonthlyPaymentBreakdownCustomerDM'
import { HomeSearchFooter } from '../../../Components/homeSearch/HomeSearchFooter/HomeSearchFooter'

export const MSG = defineMessages({
  backToCalculators: {
    id: 'MortgageCalculator.backToCalculators',
    defaultMessage: 'View all calculators'
  },
  estimateMonthlyPayment: {
    id: 'MortgageCalculator.estimateMonthlyPayment',
    defaultMessage: 'Estimate your monthly payment'
  },
  seeWhatYouWouldPay: {
    id: 'MortgageCalculator.seeWhatYouWouldPay',
    defaultMessage: "See what you would need to pay each month if you buy that home you're eyeing."
  },
  enterHomePrice: {
    id: 'MortgageCalculator.enterHomePrice',
    defaultMessage: 'Enter your target home price'
  },
  calculate: {
    id: 'MortgageCalculator.calculate',
    defaultMessage: 'Calculate monthly payment'
  }
})

export interface MortgageCalculatorProps {
  match: match<{ buyerId?: string }>
}

export const MortgageCalculator = ({ match }: MortgageCalculatorProps) => {
  const intl = useIntl()
  const { theme } = useTheme()
  const isBuyer = useSelector(selectClientIsBuyer)
  const buyerInfo: BuyerInfo = useSelector(selectBuyerInfo)
  const [showMonthlyPayment, setShowMonthlyPayment] = useState(false)
  const [targetHomePrice, setTargetHomePrice] = useState(
    isBuyer && buyerInfo?.affordability ? buyerInfo.affordability : null
  )

  return (
    <AppLayout loading={false} enableAgentDropdown match={match}>
      <Box mb={24}>
        {!showMonthlyPayment && (
          <Box
            mb={16}
            px={[5, null, 20]}
            py={[5, null, 10]}
            backgroundSize='cover'
            backgroundPosition='left'
            backgroundImage={theme.isLightThemeType ? monthlyPaymentBgLight : monthlyPaymentBg}
          >
            <Flex mb={16} mx='auto' maxWidth='1400px' direction='column' gap={4}>
              <Heading as='h1' size={['md', null, null, 'xl']} textAlign='left' fontWeight={800} m={0}>
                {intl.formatMessage(MSG.estimateMonthlyPayment)}
              </Heading>
              <Text size='sm' m={0} mb={4} fontWeight={600}>
                {intl.formatMessage(MSG.seeWhatYouWouldPay)}
              </Text>
              <Card size='lg'>
                <CardBody>
                  <FormControl>
                    <FormLabel>{intl.formatMessage(MSG.enterHomePrice)}</FormLabel>
                    <Flex gap={2} direction={['column', null, null, 'row']} alignItems='center'>
                      <CurrencyInput
                        name='targetHomePrice'
                        value={targetHomePrice?.toString()}
                        size={['sm', null, null, 'md']}
                        // @ts-ignore
                        onChange={val => setTargetHomePrice(val)}
                        tracking={{
                          guid: 'ph6xQuciNysQuWpcwoKBtw',
                          descriptor: 'Enter Target Home Price',
                          ui_context: 'Mortgage Calculator Page'
                        }}
                      />
                      <Button
                        flex='1 0 auto'
                        colorScheme='brand'
                        mt={[2, null, null, 0]}
                        isDisabled={!targetHomePrice}
                        size={['sm', null, null, 'md']}
                        width={['100%', null, null, 'auto']}
                        onClick={() => setShowMonthlyPayment(true)}
                        rightIcon={<Icon name='circle-arrow-right-bts' />}
                        tracking={{
                          guid: 'mkCZayeh7ovixompHBmsaP',
                          ui_context: 'Mortgage Calculator Page',
                          descriptor: 'Calculate Monthly Payment'
                        }}
                      >
                        {intl.formatMessage(MSG.calculate)}
                      </Button>
                    </Flex>
                  </FormControl>
                </CardBody>
              </Card>
            </Flex>
          </Box>
        )}
        {showMonthlyPayment && (
          <Box px={[5, null, 20]} py={[5, null, 10]}>
            <Box maxWidth='800px' margin='auto'>
              <EstimatedMonthlyPayment isCalculatorsPage={true} homePrice={targetHomePrice} />
            </Box>
          </Box>
        )}
        <EstimatedMonthlyPaymentBreakdownCustomerDM buyerId={match.params.buyerId} eventSource='calculators' />
        <Flex mt={12}>
          <LinkButton
            mx='auto'
            flex='0 0 auto'
            size={['sm', null, null, 'md']}
            onClick={() => history.push(SEARCH_PATH_HELPERS.calculators.buildPath(match.params.buyerId))}
            tracking={{
              guid: 't3iBuSRZ3M3qf4UtYhmLVg',
              descriptor: 'View all calculators',
              ui_context: 'Mortgage Calculator Page'
            }}
          >
            {intl.formatMessage(MSG.backToCalculators)}
          </LinkButton>
        </Flex>
      </Box>
      <HomeSearchFooter />
    </AppLayout>
  )
}
